import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { updateProfiles } from 'flow/actions/profile-actions';
import Page from 'flow/components/Setting/Page';
import CallRecordingSelection from 'flow/components/settings/call-recording/CallRecordingSelection';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { CallRecordingSettings, SettingsType } from 'flow/types/CallRecording';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { SettingsPerProfile } from 'flow/types/Location';
import { routes } from 'flow/types/routes';
import {
    getCallRecordingValue,
    getRecordAndMonitoringSettings,
    getUpdatedProfileDTO,
} from 'flow/utils/call-recording-monitoring-utils';
import { generatePath } from 'flow/utils/router-utils';

const CallRecordingAndMonitoringSettings = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<SettingsPerProfile>();
    const { openModal } = useModal();

    const { extensionKey, settingType } = useParams<{
        extensionKey: ExtensionKey;
        settingType: SettingsType;
    }>();
    const { profiles } = useProfiles(extensionKey);

    const currentSettings = getRecordAndMonitoringSettings(settingType);

    const [editPerProfile, setEditPerProfile] = useState(
        currentSettings.profilesVary(profiles),
    );

    const [storingOption, setStoringOption] = useState(null);

    useEffect(() => {
        if (history.location.state?.editPerProfile) {
            setEditPerProfile(history.location.state?.editPerProfile);
        } else {
            setEditPerProfile(currentSettings.profilesVary(profiles));
        }
    }, [profiles]);

    if (!profiles?.size) {
        return <Loading />;
    }

    const resetAllProfiles = () => {
        const firstOption = getCallRecordingValue(
            profiles.first(),
            settingType,
            true,
        );
        handleOptionSelected(firstOption);
        setEditPerProfile(false);
    };

    const confirmDescription = (
        <div
            dangerouslySetInnerHTML={{
                __html: i18next.t('settings.reset.warning.general', {
                    currentSettings: i18next.t(currentSettings.headerName),
                    firstProfileName: profiles.first().get('description'),
                }),
            }}
        />
    );

    const handleToggleEditByProfile = (value: boolean) => {
        if (currentSettings.profilesVary(profiles) && !value) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.reset.warning.general.Title'),
                description: confirmDescription,
                handleAccept: resetAllProfiles,
            });
        } else {
            setEditPerProfile(value);
        }
    };

    const footer = (
        <>
            <p>{i18next.t('settings.profile_edit')}</p>
            <Toggle
                value={editPerProfile}
                onChange={handleToggleEditByProfile}
                text={i18next.t('settings.profile_edit')}
            />
        </>
    );

    const handleOptionSelected = (value: CallRecordingSettings) => {
        setStoringOption(value);
        const profileDTOs = profiles.map((profile) =>
            getUpdatedProfileDTO(profile, value, settingType),
        );

        dispatch(
            updateProfiles(extensionKey, profileDTOs, () =>
                setStoringOption(null),
            ),
        );
    };

    return (
        <Page
            header={currentSettings.headerName}
            footer={footer}
            onClickBack={() =>
                history.push(
                    generatePath(
                        routes['settings.call-recording-and-monitoring'],
                        {
                            extensionKey,
                        },
                    ),
                )
            }
        >
            {editPerProfile ? (
                <>
                    {profiles.map((profile) => (
                        <RouteLink
                            key={profile.get('key')}
                            to={generatePath(
                                routes[
                                    'settings.call-recording-and-monitoring.settings.profileKey'
                                ],
                                {
                                    extensionKey,
                                    settingType: settingType,
                                    profileKey: profile.get('key'),
                                },
                            )}
                            className="py-4"
                            description={i18next.t(
                                `settings.call_recording.${getCallRecordingValue(profile, settingType, true)}`,
                            )}
                        >
                            {profile.get('description')}
                        </RouteLink>
                    ))}
                </>
            ) : (
                <CallRecordingSelection
                    profile={profiles.first()}
                    onSelected={handleOptionSelected}
                    settingsType={settingType}
                    storingOption={storingOption}
                />
            )}
        </Page>
    );
};

export default CallRecordingAndMonitoringSettings;
